import { DMS_BASE_COLOR_HEX, DMS_BASE_COLOR_HSL, SETTINGS_KEYS } from '../config/environment'
import { loadLanguage } from '../i18n/config'
import { mapBackendToI18nCode } from '../i18n/languages'
import { BRIGHTNESS, estimateBrightnessForColor, hexToHSL } from '../utils/color'
import { apiService } from './api.service'

interface SettingsPayload {
  settings: Record<string, any>
}

interface UiSettings {
  settings: Record<string, any>
  user: any
  permissions: any[]
  organizations?: Array<{ id: number; name: string; is_default?: boolean }>
  currentOrganizationId?: number | null
}

class SettingsServiceClass {
  private static readonly LEGACY_THEME_PREFS_KEY = 'app_theme_preferences'
  private static readonly THEME_PREFS_PREFIX = 'app_theme_preferences_org_'
  private settings: Record<string, any> = {}
  private cachedUser: any = {}
  private cachedPermissions: any[] = []
  private cachedOrganizations: Array<{ id: number; name: string; is_default?: boolean }> = []
  private cachedCurrentOrganizationId: number | null = null
  private listeners: Set<() => void> = new Set()
  private isInitialized: boolean = false
  private initializationPromise: Promise<UiSettings> | null = null

  private toBoolean(value: any): boolean {
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true'
    }
    if (typeof value === 'number') {
      return value === 1
    }
    return Boolean(value)
  }

  private getThemePrefsStorageKey(orgId?: number | null): string {
    const resolvedOrgId = orgId ?? this.cachedCurrentOrganizationId
    if (typeof resolvedOrgId === 'number' && Number.isFinite(resolvedOrgId)) {
      return `${SettingsServiceClass.THEME_PREFS_PREFIX}${resolvedOrgId}`
    }
    return SettingsServiceClass.LEGACY_THEME_PREFS_KEY
  }

  private persistThemePrefs(options: { useSystem?: boolean; enabled?: boolean; themeColor?: string }, orgId?: number | null) {
    const key = this.getThemePrefsStorageKey(orgId)
    localStorage.setItem(
      key,
      JSON.stringify({
        useSystem: Boolean(options.useSystem),
        enabled: Boolean(options.enabled),
        themeColor: options.themeColor || null,
      })
    )
  }

  getPersistedThemePrefs(orgId?: number | null): { useSystem: boolean; enabled: boolean; themeColor: string | null } | null {
    try {
      const key = this.getThemePrefsStorageKey(orgId)
      const stored = localStorage.getItem(key) || localStorage.getItem(SettingsServiceClass.LEGACY_THEME_PREFS_KEY)
      if (!stored) {
        return null
      }
      return JSON.parse(stored)
    } catch (error) {
      return null
    }
  }

  subscribe(callback: () => void): () => void {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  private notifyListeners(): void {
    this.listeners.forEach((callback) => callback())
  }

  async initializeSettings(forceRefresh: boolean = false): Promise<UiSettings> {
    if (this.isInitialized && !forceRefresh) {
      return {
        settings: this.settings,
        user: this.cachedUser,
        permissions: this.cachedPermissions,
        organizations: this.cachedOrganizations,
        currentOrganizationId: this.cachedCurrentOrganizationId,
      }
    }

    if (this.initializationPromise && !forceRefresh) {
      return this.initializationPromise
    }

    this.initializationPromise = this.fetchSettings()
    const result = await this.initializationPromise
    this.initializationPromise = null
    return result
  }

  private async fetchSettings(): Promise<UiSettings> {
    try {
      const response = await apiService.get<UiSettings>('ui_settings/')

      Object.assign(this.settings, response.data.settings)
      this.cachedUser = response.data.user
      this.cachedPermissions = response.data.permissions
      this.cachedOrganizations = response.data.organizations || []
      this.cachedCurrentOrganizationId = response.data.currentOrganizationId ?? null
      this.isInitialized = true
      this.notifyListeners()

      return response.data
    } catch (error: any) {
      this.isInitialized = true
      throw error
    }
  }

  getCurrentOrganizationId(): number | null {
    return this.cachedCurrentOrganizationId
  }

  get(key: string): any {
    let settingObj = this.settings
    const keys = key.replace('general-settings:', '').split(':')
    for (const keyPart of keys) {
      const cleanKey = keyPart.replace(/-/g, '_')
      if (settingObj && settingObj.hasOwnProperty(cleanKey)) {
        settingObj = settingObj[cleanKey]
      } else {
        return undefined
      }
    }
    return settingObj
  }

  getBoolean(key: string, defaultValue: boolean = false): boolean {
    const value = this.get(key)
    if (value === undefined) {
      return defaultValue
    }
    return this.toBoolean(value)
  }

  set(key: string, value: any): void {
    let settingObj = this.settings
    const keys = key.replace('general-settings:', '').split(':')
    keys.forEach((keyPart, index) => {
      keyPart = keyPart.replace(/-/g, '_')
      if (!settingObj.hasOwnProperty(keyPart)) settingObj[keyPart] = {}
      if (index === keys.length - 1) settingObj[keyPart] = value
      else settingObj = settingObj[keyPart]
    })

    this.notifyListeners()
  }

  async storeSettings(): Promise<void> {
    const payload: SettingsPayload = {
      settings: this.settings,
    }
    await apiService.post('ui_settings/', payload)
    this.notifyListeners()
  }

  async updateDashboardViewsSort(viewIds: number[]): Promise<void> {
    this.set(SETTINGS_KEYS.DASHBOARD_VIEWS_SORT_ORDER, [...new Set(viewIds)])
    return this.storeSettings()
  }

  async updateSidebarViewsSort(viewIds: number[]): Promise<void> {
    this.set(SETTINGS_KEYS.SIDEBAR_VIEWS_SORT_ORDER, [...new Set(viewIds)])
    return this.storeSettings()
  }

  async setLanguage(languageCode: string): Promise<void> {
    this.set(SETTINGS_KEYS.LANGUAGE, languageCode)

    if (languageCode) {
      const i18nCode = mapBackendToI18nCode(languageCode)
      await loadLanguage(i18nCode)
      localStorage.setItem('app_language', i18nCode)
    }
  }

  async updateAppearanceSettings(
    darkModeUseSystem?: boolean,
    darkModeEnabled?: boolean,
    themeColor?: string | null,
    persistPreference: boolean = true
  ): Promise<void> {
    const html = document.documentElement

    const useSystem =
      darkModeUseSystem !== undefined ? this.toBoolean(darkModeUseSystem) : this.toBoolean(this.get(SETTINGS_KEYS.DARK_MODE_USE_SYSTEM))

    const enabled = darkModeEnabled !== undefined ? this.toBoolean(darkModeEnabled) : this.toBoolean(this.get(SETTINGS_KEYS.DARK_MODE_ENABLED))

    const resolvedThemeColor = themeColor !== undefined ? themeColor : this.get(SETTINGS_KEYS.THEME_COLOR)

    if (useSystem) {
      html.setAttribute('data-bs-theme', 'auto')
      if (typeof window !== 'undefined' && window.matchMedia) {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        document.body.classList.toggle('primary-dark', prefersDark)
        document.body.classList.toggle('primary-light', !prefersDark)
      }
    } else if (enabled) {
      html.setAttribute('data-bs-theme', 'dark')
      document.body.classList.add('primary-dark')
      document.body.classList.remove('primary-light')
    } else {
      html.setAttribute('data-bs-theme', 'light')
      document.body.classList.add('primary-light')
      document.body.classList.remove('primary-dark')
    }

    if (persistPreference) {
      this.persistThemePrefs({ useSystem, enabled, themeColor: resolvedThemeColor || null })
    }

    if (resolvedThemeColor) {
      this.applyThemeColor(resolvedThemeColor)
    } else {
      this.clearThemeColor()
    }
  }

  private applyThemeColor(color: string): void {
    const root = document.documentElement

    const hsl = hexToHSL(color)
    const bgBrightnessEstimate = estimateBrightnessForColor(color)

    if (bgBrightnessEstimate === BRIGHTNESS.DARK) {
      document.body.classList.add('primary-dark')
      document.body.classList.remove('primary-light')
    } else {
      document.body.classList.add('primary-light')
      document.body.classList.remove('primary-dark')
    }

    root.style.setProperty('--pngx-primary', `${hsl.h * 360}, ${hsl.s * 100}%`)
    root.style.setProperty('--pngx-primary-lightness', `${hsl.l * 100}%`)

    this.updateMetaThemeColor(color)
  }

  private clearThemeColor(): void {
    const root = document.documentElement

    const bgBrightnessEstimate = estimateBrightnessForColor(DMS_BASE_COLOR_HEX)

    if (bgBrightnessEstimate === BRIGHTNESS.DARK) {
      document.body.classList.add('primary-dark')
      document.body.classList.remove('primary-light')
    } else {
      document.body.classList.add('primary-light')
      document.body.classList.remove('primary-dark')
    }

    root.style.setProperty('--pngx-primary', `${DMS_BASE_COLOR_HSL.h}, ${DMS_BASE_COLOR_HSL.s}%`)
    root.style.setProperty('--pngx-primary-lightness', `${DMS_BASE_COLOR_HSL.l}%`)

    this.updateMetaThemeColor(DMS_BASE_COLOR_HEX)
  }

  private updateMetaThemeColor(color: string): void {
    let metaTag = document.querySelector('meta[name="theme-color"]')
    if (!metaTag) {
      metaTag = document.createElement('meta')
      metaTag.setAttribute('name', 'theme-color')
      document.head.appendChild(metaTag)
    }
    metaTag.setAttribute('content', color)
  }
}

export const settingsService = new SettingsServiceClass()
